/**
 * TanStack Query hooks for `bulk-lp-upload`.
 *
 * 2-step flow (dry-run -> commit):
 *   - usePreviewUpload:  POST / CSV text, returns jobId + parsed rows + errors.
 *   - useJobDetail:      GET /job/:jobId, fetched after the user clicks Commit.
 *   - useCampaignJobs:   GET /campaign/:campaignId, list of jobs for history.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	previewUpload,
	getJob,
	listJobsForCampaign,
	bulkLpUploadQueryKeys,
	type BulkLpPreviewResponse,
	type BulkLpJobDetail,
	type BulkLpCampaignListResponse,
	type PreviewBody,
} from "./api";

export function usePreviewUpload(): UseMutationResult<
	BulkLpPreviewResponse,
	Error,
	PreviewBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: previewUpload,
		onSuccess: (_data, vars) => {
			qc.invalidateQueries({
				queryKey: bulkLpUploadQueryKeys.campaign(vars.campaignId),
			});
		},
	});
}

export function useJobDetail(
	jobId: string | null,
): UseQueryResult<BulkLpJobDetail, Error> {
	return useQuery({
		queryKey: jobId ? bulkLpUploadQueryKeys.job(jobId) : ["bulk-lp-upload", "job", "__none__"],
		queryFn: () => getJob(jobId as string),
		enabled: jobId != null && jobId.length > 0,
	});
}

export function useCampaignJobs(
	campaignId: string | null,
): UseQueryResult<BulkLpCampaignListResponse, Error> {
	return useQuery({
		queryKey: campaignId
			? bulkLpUploadQueryKeys.campaign(campaignId)
			: ["bulk-lp-upload", "campaign", "__none__"],
		queryFn: () => listJobsForCampaign(campaignId as string, { limit: 10 }),
		enabled: campaignId != null && campaignId.length > 0,
	});
}
